import { io, Socket } from 'socket.io-client'
import { mapPaymentOrderToOrder } from './orderService'
import type { Order, OrderSearchParams } from '@/types/order.types'
import type { PaymentOrder } from './paymentOrderService'

interface PaymentOrderPayload {
  type: 'payment_created' | 'payment_updated'
  data: Partial<PaymentOrder>
  timestamp: number
}

interface PaymentErrorPayload {
  type: 'channelError' | 'deviceError' | string
  value: string
  count: number
  date: number
}

class PaymentOrderSocketService {
  private socket: Socket | null = null
  private isConnected = false
  private connectionRefCount = 0
  private connectPromise: Promise<void> | null = null
  private listeners: Map<string, Set<Function>> = new Map()
  private messageQueue: any[] = []

  /**
   * Initialize and connect to the WebSocket server
   */
  connect(clientId?: string): Promise<void> {
    this.connectionRefCount += 1

    if (this.isSocketConnected()) {
      return Promise.resolve()
    }

    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = new Promise((resolve, reject) => {
      try {
        const query: any = {}
        if (clientId) {
          query.clientId = clientId
        }

        const apiLiveAppBaseUrl = import.meta.env.VITE_LIVE_API_BASE_URL || 'http://192.168.1.173:3000'
        const paymentOrdersUrl = apiLiveAppBaseUrl + '/payment-orders'

        if (!this.socket) {
          this.socket = io(paymentOrdersUrl, {
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5,
            query,
          })

          this.socket.on('disconnect', () => {
            this.isConnected = false
          })

          this.socket.on('payment_order_event', (payload: PaymentOrderPayload) => {
            this.handlePaymentOrderEvent(payload)
          })

          this.socket.on('payment_error_event', (payload: PaymentErrorPayload) => {
            this.emit('payment:error', payload)
          })
        }

        this.socket.on('connect', () => {
          // console.log('✅ Connected to payment orders WebSocket')
          // console.log('📡 Connection URL:', paymentOrdersUrl)
          // console.log('🆔 Socket ID:', this.socket?.id)
          this.isConnected = true
          this.connectPromise = null
          this.processMessageQueue()
          resolve()
        })

        this.socket.on('connect_error', (error) => {
          this.connectPromise = null
          reject(error)
        })

        if (!this.socket.connected) {
          this.socket.connect()
        }
      } catch (error) {
        this.connectPromise = null
        reject(error)
      }
    })

    return this.connectPromise
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.connectionRefCount = Math.max(0, this.connectionRefCount - 1)
    if (this.connectionRefCount > 0) return

    if (this.socket) {
      this.socket.disconnect()
      this.socket.removeAllListeners()
      this.socket = null
      this.isConnected = false
    }

    this.connectPromise = null
  }

  /**
   * ecoute l'event 
   */
  private handlePaymentOrderEvent(payload: PaymentOrderPayload): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    // console.log(`📨 Payment Order Event Received: ${payload.type.toUpperCase()}`)
    // console.log('🕐 Timestamp:', new Date(payload.timestamp).toLocaleString())
    // console.log('📊 Raw Payload Data:', payload.data)

    const order = mapPaymentOrderToOrder(payload.data as PaymentOrder)
    // console.log('🔄 Transformed Order:', order)

    if (payload.type === 'payment_created') {
      // console.log('✨ Emitting payment:created event...')
      this.emit('payment:created', order, payload)
      // console.log('✅ payment:created emitted to', this.listeners.get('payment:created')?.size || 0, 'listener(s)')
    } else if (payload.type === 'payment_updated') {
      // console.log('🔄 Emitting payment:updated event...')
      this.emit('payment:updated', order, payload)
      // console.log('✅ payment:updated emitted to', this.listeners.get('payment:updated')?.size || 0, 'listener(s)')
    }
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
  }

  /**
   * Register a listener for a specific event
   */
  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
  }

  /**
   * Unregister a listener for a specific event
   */
  off(event: string, callback: Function): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)!.delete(callback)
    }
  }

  /**
   * Emit an event to all registered listeners
   */
  private emit(event: string, ...args: any[]): void {
    const listenerCount = this.listeners.get(event)?.size || 0
    if (listenerCount === 0) {
      // console.warn(`⚠️ No listeners registered for event: ${event}`)
      return
    }
    
    if (this.listeners.has(event)) {
      let listenerIndex = 0
      this.listeners.get(event)!.forEach((callback) => {
        listenerIndex += 1
        try {
          callback(...args)
        } catch (error) {
          console.error(`  └─ ❌ Error in listener ${listenerIndex} for event ${event}:`, error)
        }
      })
    }
  }

  /**
   * Add message to queue if not connected
   */
  private addToMessageQueue(message: any): void {
    this.messageQueue.push(message)
  }

  /**
   * Process queued messages when connection is established
   */
  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      this.messageQueue.shift()
    }
  }

  /**
   * Check if connected to WebSocket
   */
  isSocketConnected(): boolean {
    return this.isConnected && !!this.socket?.connected
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id
  }
}

// Create singleton instance
export const paymentOrderSocketService = new PaymentOrderSocketService()

export type { PaymentOrderPayload, PaymentErrorPayload }
